import React, { useContext } from 'react';
import { get2DKeyHandler } from 't-lib-ui/structure';
import { Spinner } from 't-lib-ui/widgets';
import { QueryStatus } from 't-lib-ui';
import { AppState } from '../context/general';
import { useGetPublicFeeds } from '../connector/api';
import type { HomeFeed, RestCatalog, RestUserFeed } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { postStatsBadges } from '../schema-copy/post-stats-badges';
import ChevronLeftIcon from './ChevronLeftIcon';
import { HeaderCrumbsForm, makeCrumb, homeCrumb, CurrentCrumb } from './HeaderCrumbs';
import { CategoryIcon, categoryColorMap } from './CategoryIcons';
import { formatCategory } from '../utils/formatting';
import './AddFeedBrowse.css';

// Building blocks of the Add Feed start page: section headers, the feed lists behind the
// "Collections" tiles, and the converter that lets catalog entries use the Home page's DiscoverTile.

export type AddFeedListId = 'recent' | 'popular' | 'random' | 'frequent';

/** The collections whose feeds come from the hourly onboarding file, and its field for each. */
export const ONBOARDING_LISTS: Partial<Record<AddFeedListId, 'latestFeeds' | 'randomFeeds' | 'highFrequencyFeeds'>> = {
  recent: 'latestFeeds',
  random: 'randomFeeds',
  frequent: 'highFrequencyFeeds',
};

const SparkleIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3.5l1.6 4.4 4.4 1.6-4.4 1.6L10 15.5l-1.6-4.4L4 9.5l4.4-1.6z" />
    <path d="M18 14l.8 2.2 2.2.8-2.2.8L18 20l-.8-2.2-2.2-.8 2.2-.8z" />
  </svg>
);

const FlameIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21c-3.6 0-6-2.5-6-5.8 0-3.6 2.7-5.6 3.6-9.2 1.9 1.4 2.6 3.2 2.6 4.8 1-.6 1.6-1.7 1.8-3 1.9 1.8 4 4.4 4 7.4 0 3.3-2.4 5.8-6 5.8z" />
    <path d="M12 21c-1.5 0-2.6-1.1-2.6-2.6 0-1.6 1.2-2.5 1.7-3.9 1.6.9 3.5 2.2 3.5 3.9 0 1.5-1.1 2.6-2.6 2.6z" />
  </svg>
);

const DiceIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <circle cx="9" cy="9" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="9" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9" cy="15" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="15" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

const BoltIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2.5L5 13.5h6l-1 8 8-11h-6z" />
  </svg>
);

export const ADD_FEED_LISTS: { id: AddFeedListId; title: string; color: string; icon: React.ReactNode }[] = [
  { id: 'recent', title: 'Recently added', color: '#7C4DFF', icon: <SparkleIcon /> },
  { id: 'popular', title: 'Most popular', color: '#C0392B', icon: <FlameIcon /> },
  // Both regenerated hourly by the onboarding job.
  { id: 'random', title: 'Random', color: '#16A085', icon: <DiceIcon /> },
  { id: 'frequent', title: 'High frequency', color: '#E67E22', icon: <BoltIcon /> },
];

export const addFeedListTitle = (id: string) => ADD_FEED_LISTS.find(l => l.id == id)?.title;

/** A catalog entry in the shape DiscoverTile renders (what the Home content generator produces server side). */
export const catalogToHomeFeed = (f: RestCatalog): HomeFeed => ({
  id: f.id,
  title: f.shortTitle || f.title,
  iconUrl: getFeedIconUrl(f, 128),
  iconUrl2x: getFeedIconUrl(f, 256),
  circularIcon: f.circularIcon,
  types: postStatsBadges(f.postStats, f.category, f.textType),
  postFrequency: f.postFrequency,
  paid: !!f.paid,
});

const linkKeyHandler = get2DKeyHandler((el: any) => el.click()) as any;

export const BrowseSectionHeader: React.FC<{ title: string; seeAll?: string; onSeeAll?: () => void }> = ({ title, seeAll, onSeeAll }) => (
  <div className="addFeedSectionHeader">
    <h2 className="addFeedSectionTitle">{title}</h2>
    {seeAll && onSeeAll && (
      <button type="button" tabIndex={0} className="addFeedSeeAll flowFocussable flow2d" onClick={onSeeAll} onKeyDown={linkKeyHandler}>
        {seeAll}
      </button>
    )}
  </div>
);

/** Page header title of Add Feed's sub pages (a HeaderCrumbs FlowForm, see AddFeedPage):
 *  "‹ Add Feed / [Parent /] Current". Entered from Home's Categories section (fromHome), the first
 *  level is "[home icon] Home" instead and leads back there. */
type CrumbsProps = { fromHome?: boolean; onNavigate: (path: string) => void };

const AddFeedCrumbs: React.FC<CrumbsProps & { children: React.ReactNode }> = ({ fromHome, onNavigate, children }) => {
  const crumb = makeCrumb(onNavigate);
  return (
    <HeaderCrumbsForm>
      {fromHome ? crumb('/a/home', homeCrumb) : crumb('/a/add', <><ChevronLeftIcon size={18} />Add Feed</>)}
      {children}
    </HeaderCrumbsForm>
  );
};

/** Crumbs of a page without levels between Add Feed and itself ("All topics", a collection). */
export const TitleCrumbs: React.FC<CrumbsProps & { title: string }> = ({ title, ...props }) => (
  <AddFeedCrumbs {...props}>
    <CurrentCrumb>{title}</CurrentCrumb>
  </AddFeedCrumbs>
);

/** Crumbs of a category page: "… / [All topics /] Parent / [icon] Category" ("All topics" when the
 *  topic was opened from that page). */
export const CategoryCrumbs: React.FC<CrumbsProps & { category: string; viaAllTopics?: boolean }> = ({ category, viaAllTopics, ...props }) => {
  const parent = category.includes('/') ? category.slice(0, category.lastIndexOf('/')) : undefined;
  const label = formatCategory(category) ?? category;
  const crumb = makeCrumb(props.onNavigate);
  return (
    <AddFeedCrumbs {...props}>
      {viaAllTopics && crumb('/a/add/topics', 'All topics')}
      {parent && crumb(`/a/add/${parent}`, formatCategory(parent) ?? parent)}
      <CurrentCrumb icon={
        <span className="headerCrumbIcon" style={{ background: categoryColorMap[category] || categoryColorMap[parent ?? ''] || 'var(--gray-400)' }}>
          <CategoryIcon category={category} size={16} color="#fff" />
        </span>
      }>
        {label.slice(label.lastIndexOf('/') + 1)}
      </CurrentCrumb>
    </AddFeedCrumbs>
  );
};

export const CollectionTile: React.FC<{ title: string; color: string; icon: React.ReactNode; onOpen: () => void }> = ({ title, color, icon, onOpen }) => (
  <div
    className="addFeedCollectionTile flowFocussable flow2d"
    style={{ '--coll-color': color } as React.CSSProperties}
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={get2DKeyHandler(onOpen)}
  >
    <span className="addFeedCollectionName">{title}</span>
    <span className="addFeedCollectionIcon">{icon}</span>
  </div>
);

/** "Recently added": the newest verified public feeds from the onboarding file, loaded as full
 *  feed records so they can be shown as FeedTiles. Mount only once the ids are known. */
export const RecentFeedsList: React.FC<{ feedIds: string[]; renderFeeds: (feeds: (RestUserFeed | RestCatalog)[]) => React.ReactNode }> = ({ feedIds, renderFeeds }) => {
  const appState = useContext(AppState);
  const feeds = useGetPublicFeeds(feedIds, appState.state.authData);
  if (feeds.status == QueryStatus.Loading || feeds.status == QueryStatus.Pending)
    return <div className="addFeedListLoading"><Spinner spinning={true} /></div>;
  return <>{renderFeeds(feeds.data ?? [])}</>;
};
